import $ from "jquery";

import toNativePromise from "JIX/utils/toNativePromise.js";
import RequestError from "JIX/errors/RequestError.js";
import ApiValidationError from "JIX/errors/ApiValidationError.js";
import ApiConflictError from "JIX/errors/ApiConflictError.js";
import { unref, computed } from 'vue';
import { useMatchesBreakpoint } from 'JIX/breakpointListener.js';

/**
 * Randomly shuffles an array
 *
 * @template {unknown} T
 * @param {Array<T>} array The array to shuffle
 * @returns {Array<T>} The shuffled array
 */
export function shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        const temp = array[i];
        array[i] = array[j];
        array[j] = temp;
    }
    return array;
}

/**
 * Remove duplicate entries from an array
 *
 * @template {unknown} T
 * @param {Array<T>} array The array to filter
 * @returns {Array<T>} The filtered array
 */
export function uniqueArray(array) {
    return array.filter((value, index, self) => self.indexOf(value) === index);
}

/**
 * Return a string with the concatenation of individual name parts,
 * sepparated by spaces.
 * Expects an object with the properties `fullname`, `middlename` and `lastname`
 * (which may be undefined or null).
 *
 * @param {Object} name The name parts object
 * @param {string?} name.firstname
 * @param {string?} name.middlename
 * @param {string?} name.lastname
 * @returns {string} The concatenated name
 */
export function concatName({ firstname, middlename, lastname }) {
    return [firstname, middlename, lastname].join(" ").trim();
}

/**
 * Performs named substitution of parameters.
 *
 * Replaces all occurences of the key.
 * Equivalent to __x() from Gettext, * but without translations.
 *
 * @param {string} str - The format string
 * @param {Object} args - A map from named parameter to string
 * @returns {string} Returns the format-substituted string
 * @example
 * format("Hello {name}, you {title}!", { name: "Dave", title: "chap" })
 */
export function format(str, args) {
    return Object.keys(args).reduce(
        (acc, key) => acc.replace(new RegExp("{" + key + "}", 'g'), args[key]),
        str
    );
}

/**
 * Get a number formatter for currencies
 *
 * @param {string} currency The currency format to use
 * @param {string} [locale] The locale to use, defaults to current locale
 * @returns {Intl.NumberFormat} The formatter
 */
export function getCurrencyFormatter(currency, locale = getLocale()) {
    return new Intl.NumberFormat(locale, { style: 'currency', currency, minimumFractionDigits: 0 });
}


/**
 * Get a number formatter for percentages
 *
 * @param {string} [locale] The locale to use, defaults to current locale
 * @returns {Intl.NumberFormat} The formatter
 */
export function getPercentFormatter(locale = getLocale()) {
    return new Intl.NumberFormat(locale, { style: 'percent', minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Get a generic number formatter
 *
 * @param {string} [locale] The locale to use, defaults to current locale
 * @returns {Intl.NumberFormat} The formatter
 */
export function getNumberFormatter(locale = getLocale()) {
    return new Intl.NumberFormat(locale);
}

/**
 * Get a number formatter for formatting large numbers
 *
 * @param {String?} locale - The locale to use for formatting, defaults to the current locale
 */
export function getCompactNumberFormatter(locale = getLocale()) {
    return new Intl.NumberFormat(locale, {
        notation: "compact",
        maximumFractionDigits: 1
    });
}

const byteConvRatio = {
    'B':  0,
    'KB': 1,
    'MB': 2,
    'GB': 3,
    'TB': 4,
};

/**
 * Format byte sizes
 *
 * Converts between byte sizes
 *
 * @param {number} size The byte size to format
 * @param {keyof byteConvRatio} [toType="MB"] Type of byte to format to (default: 'MB')
 * @param {keyof byteConvRatio} [fromType="B"] Type of byte to format from (default: 'B')
 * @returns {string} The formatted byte string
 */
export function formatByteSize(size, toType = 'MB', fromType = 'B') {
    let ratio = byteConvRatio[fromType] - byteConvRatio[toType];
    return (size * Math.pow(1024, ratio)).toFixed(2);
}

/**
 * Format byte sizes with automatically chosen byte format
 *
 * @param {number} size The byte size to format
 * @returns {string} The formatted byte string
 */
export function formatBytesAuto(size) {
    let resSize = size;
    let resUnit = 'B';
    for (const [unit, power] of Object.entries(byteConvRatio)) {
        let tmp = size / (1024 ** power);
        if (tmp < 1) {
            break;
        }
        resSize = tmp;
        resUnit = unit;
    }
    resSize = new Intl.NumberFormat(getLocale(), { maximumFractionDigits: 1 }).format(resSize);
    return `${resSize} ${resUnit}`;
}

/**
 * @typedef {"GET" | "POST" | "PUT" | "DELETE" | "CONNECT" | "OPTIONS" | "TRACE" | "PATCH"} HTTPMethod
 */

/**
 * Performs API call as a promise
 * @param {HTTPMethod} method - HTTP method for the the API call
 * @param {string} url - Endpoint for API call
 * @param {Object} data - Data associated with API call
 * @param {Object} options - Optional options object
 * @returns {Promise} - response from API endpoint
 */
export function api(method, url, data = {}, options = {}) {
    const json = options.json;
    delete options.json;
    const ajaxOptions = {
        method,
        url,
        data,
        traditional: true,
        headers: {},
        ...options,
    };
    if (window.Stash && window.Stash.common && window.Stash.common.csrf_token) {
        ajaxOptions.headers["X-CSRF-Token"] = window.Stash.common.csrf_token;
    }
    const hasRequestBody = !['GET', 'HEAD'].includes(method);
    if (json && hasRequestBody) {
        ajaxOptions.data = JSON.stringify(data);
        ajaxOptions.contentType ||= 'application/json';
    }

    return toNativePromise($.ajax(ajaxOptions))
        .catch(xhr => {
            if (xhr.status === 422) {
                throw new ApiValidationError(method, url, xhr);
            } else if (xhr.status === 409) {
                throw new ApiConflictError(method, url, xhr);
            } else {
                throw new RequestError(method, url, xhr);
            }
        });
}

/**
 * Construct a custom API utility function. The returned function works as the
 * api() utility with the following differences:
 *   - The `endpoint` argument can be either a string or an array. If it is an array
 *     the elements are joined with slashes.
 *   - If the `endpoint` argument doesn't start with a slash it is prefixed with the
 *     `baseUrl` given to the `makeApi` function.
 *   - The `baseOptions` object given to the `makeApi` function is merged with
 *     the options given to the returned api function with the latter having
 *     priority.
 *
 * @param {string} baseUrl
 * @param {Object} [baseOptions={}]
 * @returns {(method: HTTPMethod, endpoint: string|string[], data: Object = {}, options: Object = {}) => Promise<any>}
 *     A function to make API calls
 */
export function makeApi(baseUrl, baseOptions = {}) {
    if (!baseUrl.endsWith('/')) {
        baseUrl += '/';
    }
    return function _api(method, endpoint, data = {}, options = {}) {
        options = { ...baseOptions, ...options };
        const path = Array.isArray(endpoint) ? endpoint.join("/") : endpoint;
        const url = path.startsWith('/') ? path : baseUrl + path;
        return api(method, url, data, options);
    };
}

/**
 * Load a script by adding a script tag to the DOM
 *
 * @param {string} url The URL of the script to load
 * @param {Object} [options={}]
 * @param {Element} [options.parent] The parent element to add the script tag to
 * @param {boolean} [options.async] Mark the script as async
 * @param {boolean} [options.defer] Mark the script as deferred
 * @returns {Promise} A promise that resolves when the script is loaded
 */
export function loadScript(url, options = {}) {
    return new Promise((resolve, reject) => {
        const parentElement = options.parent || document.head;

        const script = document.createElement('script');
        script.src = url;

        script.async = !!(options.async);
        script.defer = !!(options.defer);

        script.onload = resolve;
        script.onerror = reject;

        parentElement.appendChild(script);
    });
}

/**
 * Get the current locale
 *
 * @param {Object} [options={}]
 * @param {string} [options.defaultLocale="en-GB"] - The locale to fall back to if no current locale is found (default: 'en-GB')
 * @param {Element} [options.element] - The element to perform the locale lookup on, defaults to `document.documentElement`
 * @returns {string} The current locale
 */
export function getLocale({ defaultLocale = 'en-GB', element = null } = {}) {
    element ||= document.documentElement;
    do {
        if (element.getAttribute('lang')) {
            return element.lang;
        } else {
            element = element.parentElement;
        }
    } while (element);
    return defaultLocale;
}

/**
 * Get a random integer bounded by `min` and `max`
 *
 * @param {number} [min=0] The minimum value the integer can be
 * @param {number} [max=Number.MAX_SAFE_INTEGER] The maximum value the integer can be
 * @returns {number} A random integer
 */
export function randomInteger(min = 0, max = Number.MAX_SAFE_INTEGER) {
    return Math.random() * (max - min) + min;
}

/**
 * @param {string} html The HTML string to strip
 * @returns {string} The text content of the HTML string
 */
export function stripHtml(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent;
}

/**
 * @param {string} str The string to truncate
 * @param {number} maxLength The max length of the resulting string
 * @param {string} [replacement = '…'] The string to replace the truncated part with
 * @returns {string} The truncated string
 */
export function truncateString(str, maxLength, replacement = '…') {
    if (str.length <= maxLength) {
        return str;
    }
    const cut = maxLength - replacement.length;
    return [...str].slice(0, cut).join('') + replacement;
}


/** @import { Ref } from 'vue'; */

/**
 * @param {Ref<string|string[]>} title
 */
export function setDocumentTitle(title) {
    if (title) {
        let titleRaw = unref(title);
        titleRaw = Array.isArray(titleRaw) ? titleRaw : [titleRaw];
        document.title = [...titleRaw, window.Stash.common.sitename].join(" | ");
    } else {
        document.title = window.Stash.common.sitename;
    }
}

/**
 * @param {number} ms The delay to sleep for
 */
export const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * @param {string} url The URL to use as canonical URL
 */
export function setCanonicalUrl(url) {
    let canonical = document.querySelector('link[rel=canonical]');
    if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.append(canonical);
    }
    canonical.setAttribute('href', url);

    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
        ogUrl = document.createElement('meta');
        ogUrl.setAttribute('property', 'og:url');
        document.head.append(canonical);
    }
    ogUrl.setAttribute('content', url);

    const updateHreflangElem = (elem) => {
        if ([window.Stash.common.lang_native, 'x-default'].includes(elem.hreflang)) {
            elem.href = url;
        } else {
            const localUrl = new URL(url, location.href);
            localUrl.searchParams.set('lang', elem.hreflang);
            elem.href = localUrl;
        }
    };

    const foundHreflang = new Set();
    const expectedHreflang = new Set([...window.Stash.common.lang_available, 'x-default']);
    document.querySelectorAll('link[rel=alternate][hreflang]').forEach(elem => {
        foundHreflang.add(elem.hreflang);
        updateHreflangElem(elem);
    });

    foundHreflang.difference(expectedHreflang).forEach(extra => {
        document.querySelector(`link[rel=alternate][hreflang=${extra}]`)?.remove();
    });
    expectedHreflang.difference(foundHreflang).forEach(missing => {
        const elem = document.createElement('link');
        elem.hreflang = missing;
        elem.rel = 'alternate';
        updateHreflangElem(elem);
        document.head.append(elem);
    });
}

/**
 * @param {any} value
 */
export function setMetaDescription(value) {
    let elem = document.querySelector('meta[name=description]');
    if (value) {
        if (!elem) {
            elem = document.createElement('meta');
            elem.name = 'description';
            document.head.append(elem);
        }
        elem.content = value;
    } else {
        elem?.remove();
    }
}

/**
 * Get the percentage scrolled through the document element
 *
 * @returns {number} The scrolled percentage
 */
export function scrollPercentage() {
    return (document.documentElement.scrollTop + document.body.scrollTop) / (document.documentElement.scrollHeight - document.documentElement.clientHeight);
}

/**
 * Turns weird quotation marks into normal quotation marks.
 * @param {string} string - The string to normalize
 * @returns {string} The string with normalized quotes
 */
export function normalizeQuotes(string) {
    // Replace various single quote characters with standard apostrophe
    string = string.replace(/[\u2018\u2019\u201A\u2032\u2035\uFF07]/g, "'");

    // Replace various double quote characters with standard quotation mark
    string = string.replace(/[\u201C\u201D\u201E\u2033\u2036\u2033\u2036\uFF02]/g, '"');

    return string;
}

/**
 * Sets the RSS feed URL in the document head by managing the RSS link tag.
 * @param {string|null} url - The RSS feed URL to set. If null or falsy, removes any existing RSS link tag.
 *
 * If a URL is provided:
 * - Updates the existing RSS link tag if one exists
 * - Creates a new RSS link tag if none exists
 *
 * If no URL is provided (null/falsy):
 * - Removes any existing RSS link tag
 *
 * The function looks for link tags with rel="alternate" and type="application/rss+xml".
 */
export function setRssFeedUrl(url) {
    const rel = "alternate";
    const type = "application/rss+xml";

    let rssLink = document.querySelector(`link[rel="${rel}"][type="${type}"]`);

    if (url) {
        if (!rssLink) {
            rssLink = document.createElement('link');
            rssLink.setAttribute('rel', rel);
            rssLink.setAttribute('type', type);
            document.head.append(rssLink);
        }
        rssLink.setAttribute('href', url);
    } else {
        rssLink?.remove();
    }
}

export const isMobile = computed(() => !useMatchesBreakpoint('sm').value);
export const isMedium = computed(() => useMatchesBreakpoint('md').value);
export const isLarge = computed(() => useMatchesBreakpoint('lg').value);
